import React, { useEffect, useState } from 'react'
import { createUseStyles } from 'react-jss'

import { useParams } from 'react-router-dom'
import { useAtom } from 'jotai'
import { getInitials } from '../../../utils/initials'

import { colors } from '../../../theme/palette'
import { CircleText, CircleIcon2 } from '../../ui/Circle/Circle'
import { CardColumn, Row } from '../../ui/Card/CardColumn'
import { Typography } from '../../ui/Typography/Typography'
import {
    SuitcaseFill,
    Foa,
    PersonalInfo,
    ContactLedger,
} from '../../../assets/icons'
import { CardColumnContainer } from '../../ui/Card/CardColumnContainer'
import { getMemberAfterSearch } from '../../../api/members/members'
import { Member } from '../../../models/Member'
import { Loader } from '../../ui/Loaders/Loader'
// import { EditMemberProfile } from '../../modules/EditMemberProfile'
import { AddMemberToOwnList } from '../../modules/AddMemberToOwnList'
import { buildRow } from '../MyProfile'
import { formatDate } from '../../../utils/format'
import { GmapsLink } from '../../ui/GmapsLink/GmapsLink'
import { Workplace } from '../../../models/Workplace'
import { BackLink } from '../../ui/BackLink'
import { getAge } from '../../../utils/utils'
import { L } from '../../providers/Lokalise'
import { profileAtom } from '../../../api/profile/profile'
import breakpoints from '../../../theme/breakpoints'
import { Spinner } from '../../ui/Loaders/Spinner'
import { buildEmailRow } from '../MemberProfile'

const useStyles = createUseStyles({
    container: {
        color: colors.black,
        padding: '0 24px',
        [`@media (min-width: ${breakpoints.Desktop}px)`]: {
            '&': {
                padding: 0,
            },
        },
    },
    profileHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '3rem 0',
    },
    nameContainer: {
        display: 'flex',
        flexBasis: '50%',
        alignItems: 'center',
        justifyContent: 'flex-start',
    },
    edit: {
        display: 'flex',
        flexBasis: '50%',
        alignItems: 'center',
        justifyContent: 'flex-end',
    },
    section: {
        marginBottom: '3rem',

        '& > hr': {
            opacity: '0.2',
            margin: '1.25rem 0',
        },
    },
    iconWrapper: {
        position: 'relative',
        width: '90px',
        marginRight: '30px',
    },
})

const MemberProfileFromSearch: React.FC = () => {
    const classes = useStyles()
    const params = useParams()
    const [authProfile] = useAtom(profileAtom)
    const [showImitatorModal, setShowImitatorModal] = useState(false)

    const [data, _setData] = useState<Member | undefined>(undefined)
    const init = async (id: string): Promise<unknown> => {
        const response = await getMemberAfterSearch(+id)
        const { data: profileData } = response
        const profile = Member.build(profileData)
        _setData(profile)
        return response
    }

    useEffect(() => {
        const { id } = params

        if (id) {
            init(id)
        }
    }, [params])

    const getPositionName = (): string => {
        const id = data.primaryWorkPlace.postionNumber
        const group = data.positions.find((pos) => +pos.id === +id)
        return group.name || '-'
    }

    const getGroupName = (): string => {
        const id = data.primaryWorkPlace.postionNumber
        const group = data.positions.find((pos) => +pos.id === +id)
        return group.group.name || '-'
    }

    const getPersonalInfoRows = (member: Member): Row[] => {
        return [
            buildRow(formatDate(member.birthday), 'Fødselsdag'),
            // buildRow(member.age ? `${member.age} år` : '-', 'Alder'),
            buildRow(
                member.birthday
                    ? `${getAge(member.birthday.getFullYear())} år`
                    : null,
                L('GENERISK_FORM_ALDER_LABEL')
            ),
        ]
    }

    const getWorkInfo = (workplace: Workplace): Row[] => {
        let customedGmapsUrl = ''
        if (workplace?.address) {
            const {
                addressLine1,
                addressLine2,
                addressLine3,
                postDistrict,
                postNumber,
            } = workplace?.address
            customedGmapsUrl = `https://www.google.com/maps/search/?api=1&query=${addressLine1}+${addressLine2}+${addressLine3}%2C+${postNumber}+${postDistrict}`
        }

        const rows: Row[] = []

        if (customedGmapsUrl !== '') {
            rows.push({
                label: '',
                value: '',
                useContent: !!customedGmapsUrl,
                content: !!customedGmapsUrl && (
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        <Typography
                            tag="h4"
                            size="XXS"
                            color=" rgba(0, 0, 0, 0.4)"
                        >
                            {L('GENERISK_FORM_ARBEJDSSTED_LABEL')}
                        </Typography>
                        <Typography
                            tag="p"
                            size="XS"
                            style={{ marginBottom: '10px' }}
                        >
                            {workplace.name}
                        </Typography>
                        <GmapsLink customedGmapsUrl={customedGmapsUrl} />
                    </div>
                ),
            })
        } else {
            rows.push(buildRow(workplace?.name, 'Arbejdssted'))
        }

        return [
            ...rows,
            ...[
                buildRow(getPositionName(), L('GENERISK_FORM_STILLING_LABEL')),
                buildRow(
                    workplace?.hoursPerWeek,
                    L('GENERISK_FORM_TIMETAL_LABEL')
                ),
                buildRow(getGroupName(), 'Faggruppe'),
            ],
        ]
    }

    const getContactInfoRows = (member: Member): Row[] => {
        return [
            buildRow(
                member.privateContact?.mobileShow || '-',
                L('GENERISK_FORM_PRIVAT-MOBIL_LABEL')
            ),
            buildEmailRow(
                member.privateContact?.email,
                L('GENERISK_FORM_PRIVAT-EMAIL_LABEL'),
                authProfile.isImitator,
                showImitatorModal,
                setShowImitatorModal
            ),
        ]
    }

    const buildTOR = (member: Member): JSX.Element => {
        return (
            <div>
                <Typography tag="h4" size="XXS" color=" rgba(0, 0, 0, 0.4)">
                    {L('GENERISK_FORM_TILLIDSHVERV_LABEL')}
                </Typography>
                <>
                    {member.typeOfRepresentatives.map((type) => {
                        return (
                            <Typography tag="p" size="XS">
                                {type.name}
                            </Typography>
                        )
                    })}
                </>
            </div>
        )
    }

    const getMembershipInfoRows = (member: Member): Row[] => {
        const rows = [
            buildRow(
                member.memberNumber,
                L('GENERISK_FORM_MEDLEMSNUMMER_LABEL')
            ),
            buildRow(
                member.membershipInfo.name,
                L('GENERISK_FORM_FOAAFDELING_LABEL')
            ),
        ]

        if (member.typeOfRepresentatives.length) {
            rows.push({
                label: '',
                value: '',
                useContent: true,
                content: buildTOR(member),
            })
        }

        return rows
    }

    if (!data) return <Spinner />

    return (
        <div className={`${classes.container} page-up`}>
            <BackLink to="/alle-foas-medlemmer" label="Alle FOA-medlemmer" />
            <div className={classes.profileHeader}>
                <div className={classes.nameContainer}>
                    <div className={classes.iconWrapper}>
                        <CircleIcon2 width="92px" withBorder borderWidth="6px">
                            <CircleText fontSize="30px">
                                {getInitials(data.fullName)}
                            </CircleText>
                        </CircleIcon2>
                    </div>
                    <Typography tag="h1" size="XXXXL" spacing=".2rem">
                        {data.fullName}
                    </Typography>
                </div>
                <div className={classes.edit}>
                    {data ? <AddMemberToOwnList member={data} /> : null}
                </div>
            </div>
            <div className={classes.section}>
                <hr />
                <CardColumnContainer>
                    <div>
                        <CardColumn
                            titleIcon={<PersonalInfo />}
                            titleLabel={L(
                                'ALLEMEDLEMMER_DETALJE_PERSONLIGE-OPLYSNINGER_TITEL'
                            )}
                            rows={getPersonalInfoRows(data)}
                            key="Personlige oplysninger"
                        />

                        <CardColumn
                            titleIcon={<Foa />}
                            titleLabel={L(
                                'ALLEMEDLEMMER_DETALJE_FOAMEDLEMSSKAB-TITEL'
                            )}
                            rows={getMembershipInfoRows(data)}
                            key="FOA medlemsskab"
                        />
                    </div>

                    <CardColumn
                        titleIcon={<SuitcaseFill />}
                        titleLabel={L('GENERISK_FORM_ANSÆTTELSE_TITEL')}
                        rows={getWorkInfo(data.primaryWorkPlace)}
                        key={data.primaryWorkPlace?.address?.id}
                    />

                    <CardColumn
                        iconWidth="16px"
                        titleIcon={<ContactLedger />}
                        titleLabel={L('ALLEMEDLEMMER_DETALJE_KONTAKT_TITEL')}
                        rows={getContactInfoRows(data)}
                        key="Kontakt"
                    />
                </CardColumnContainer>
            </div>
        </div>
    )
}

export { MemberProfileFromSearch }
